import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft, ExternalLink, Play } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminMediaItem } from '@/lib/api'
import { useConfirm } from '@/components/ConfirmDialog'
import { normalizeImageKitUrl } from '@lic/media-urls'

const BADGE_STYLE: Record<string, string> = {
  MV:     'bg-pink-500/15 text-pink-300 border border-pink-500/20',
  LIVE:   'bg-blue-500/15 text-blue-300 border border-blue-500/20',
  BEHIND: 'bg-yellow-500/15 text-yellow-300 border border-yellow-500/20',
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-800">
        <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  )
}

export default function MediaDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { token } = useAuth()
  const { confirm, dialog } = useConfirm()

  const [media, setMedia] = useState<AdminMediaItem | null>(
    (location.state as { media?: AdminMediaItem } | null)?.media ?? null
  )
  const [loading, setLoading] = useState(!media)

  useEffect(() => {
    if (media || !token || !id) return
    api.getAdminMedia(token)
      .then((items) => setMedia(items.find((m) => m.id === id) ?? null))
      .finally(() => setLoading(false))
  }, [media, id, token])

  const handleDelete = async () => {
    if (!media) return
    if (!await confirm(
      `"${media.title}" akan dihapus permanen dan tidak bisa dipulihkan.`,
      { title: `Hapus "${media.title}"` }
    )) return
    if (!token) return
    try {
      await api.deleteMedia(token, media.id)
      navigate(-1)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    }
  }

  if (loading) {
    return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  }

  if (!media) {
    return <div className="p-6"><p className="text-red-400 text-sm">Media not found.</p></div>
  }

  return (
    <div className="p-6">
      {/* Nav */}
      <div className="sticky top-0 z-10 bg-zinc-950 flex items-center justify-between pb-4 mb-6 border-b border-zinc-800">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          <ChevronLeft size={14} />
          Kembali
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/media/${media.id}/edit`, { state: { media } })}
            className="bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold px-4 py-2 rounded transition-colors"
          >
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="text-xs text-red-400 hover:text-red-300 transition-colors px-3 py-2 rounded hover:bg-zinc-800"
          >
            Hapus
          </button>
        </div>
      </div>

      <div className="max-w-xl mx-auto">
      {/* Thumbnail hero */}
      <div className="relative rounded-2xl overflow-hidden bg-zinc-800 mb-4">
        <div className="aspect-video w-full relative">
          {media.image_url ? (
            <img
              src={normalizeImageKitUrl(media.image_url)}
              alt={media.alt || media.title}
              className="w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-zinc-800">
              <Play size={32} className="text-zinc-600" />
            </div>
          )}

          {/* Badges overlay — top left */}
          <div className="absolute top-3 left-3 flex items-center gap-2">
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full backdrop-blur-sm ${BADGE_STYLE[media.badge] ?? 'bg-zinc-800/80 text-zinc-300 border border-zinc-700'}`}>
              {media.badge}
            </span>
            {media.is_featured && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full backdrop-blur-sm bg-yellow-500/20 text-yellow-300 border border-yellow-500/20">
                Featured
              </span>
            )}
          </div>

          {/* Duration — bottom right */}
          {media.duration && (
            <div className="absolute bottom-3 right-3">
              <span className="text-[11px] font-mono font-medium px-2 py-0.5 rounded bg-black/50 backdrop-blur-sm text-white/80">
                {media.duration}
              </span>
            </div>
          )}
        </div>

        {/* Glassmorphism title bar */}
        <div className="backdrop-blur-md bg-black/30 border-t border-white/10 px-4 py-3">
          <h1 className="text-base font-bold text-white leading-snug">{media.title}</h1>
          <p className="text-white/50 text-xs mt-0.5">{media.group_slug ?? '—'} · {media.date}</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* YouTube */}
        {media.youtube_url && (
          <a
            href={media.youtube_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between gap-3 bg-zinc-900 border border-zinc-800 hover:border-zinc-600 rounded-xl px-5 py-4 transition-colors group"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-red-500/15 border border-red-500/20 flex items-center justify-center flex-shrink-0">
                <Play size={12} className="text-red-400 fill-red-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-0.5">Tonton di</p>
                <p className="text-sm font-medium text-white group-hover:text-red-400 transition-colors">YouTube</p>
              </div>
            </div>
            <ExternalLink size={14} className="text-zinc-600 group-hover:text-red-400 transition-colors flex-shrink-0" />
          </a>
        )}

        {/* Meta */}
        <Section title="Info Teknis">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] text-zinc-500 mb-1">Media ID</p>
              <p className="text-xs text-zinc-600 font-mono break-all">{media.id}</p>
            </div>
            <div>
              <p className="text-[10px] text-zinc-500 mb-1">Group</p>
              <p className="text-xs text-zinc-400 font-mono">{media.group_slug ?? '—'}</p>
            </div>
            {media.alt && (
              <div className="col-span-2">
                <p className="text-[10px] text-zinc-500 mb-1">Alt Text</p>
                <p className="text-xs text-zinc-500">{media.alt}</p>
              </div>
            )}
          </div>
        </Section>
      </div>

      </div>
      {dialog}
    </div>
  )
}
